import { createFileRoute, redirect } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useMemo, useState } from "react";
import {
  Check,
  ChevronDown,
  Mail,
  Shield,
  Trash2,
  Users as UsersIcon,
} from "lucide-react";
import { api, type User, type UserRole } from "~/lib/api";
import { useMe } from "~/lib/auth";
import { useRelativeTime } from "~/lib/time";
import { AppShell } from "~/components/AppShell";
import { Group } from "~/components/ui/Group";
import { RowSeparator } from "~/components/ui/Row";
import { IconBadge, type IconBadgeTint } from "~/components/ui/IconBadge";
import { Skeleton } from "~/components/ui/Skeleton";
import { ConfirmDialog } from "~/components/ui/ConfirmDialog";
import { useToast } from "~/components/ui/Toast";

export const Route = createFileRoute("/_authed/users")({
  beforeLoad: async ({ context }) => {

    const me = await context.queryClient.ensureQueryData({
      queryKey: ["me"],
      queryFn: () => api.me(),
    });
    if (me.role !== "admin") {
      throw redirect({ to: "/" });
    }
  },
  component: UsersPage,
});

const ROLE_LABEL: Record<UserRole, string> = {
  owner: "Owner",
  admin: "Admin",
  support: "Support",
};

const ROLE_TINT: Record<UserRole, IconBadgeTint> = {
  owner: "blue",
  admin: "purple",
  support: "teal",
};

function UsersPage() {
  const { data: me } = useMe();
  const toast = useToast();
  const qc = useQueryClient();

  const { data: users, isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.listUsers(),
  });

  const updateRole = useMutation({
    mutationFn: ({ userID, role }: { userID: number; role: UserRole }) =>
      api.updateUserRole(userID, role),
    onMutate: async ({ userID, role }) => {

      await qc.cancelQueries({ queryKey: ["users"] });
      const prev = qc.getQueryData<User[]>(["users"]);
      qc.setQueryData<User[]>(["users"], (cur) =>
        (cur ?? []).map((u) => (u.id === userID ? { ...u, role } : u)),
      );
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["users"], ctx.prev);
      toast.error(err instanceof Error ? err.message : "Couldn't change the role.");
    },
    onSuccess: () => {
      toast.success("Role updated.");
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
    },
  });

  const deleteUser = useMutation({
    mutationFn: (userID: number) => api.deleteUser(userID),
    onSuccess: () => {
      toast.success("User deleted.");
      qc.invalidateQueries({ queryKey: ["users"] });

      qc.invalidateQueries({ queryKey: ["devices"] });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Couldn't delete the user.");
    },
  });

  const sorted = useMemo(() => {
    return [...(users ?? [])].sort((a, b) => {

      const rank = (r: UserRole) => (r === "admin" ? 0 : r === "support" ? 1 : 2);
      const dr = rank(a.role) - rank(b.role);
      if (dr !== 0) return dr;
      return a.email.localeCompare(b.email);
    });
  }, [users]);

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto pt-4 sm:pt-6 lg:pt-8">
        <header className="pb-6">
          <h1 className="text-largeTitle" style={{ color: "var(--label-primary)" }}>
            Users
          </h1>
          <div className="text-subhead mt-1" style={{ color: "var(--label-secondary)" }}>
            {users
              ? `${users.length} ${users.length === 1 ? "account" : "accounts"}`
              : "Manage account access and roles."}
          </div>
        </header>

        {isLoading && <UsersListSkeleton />}

        {!isLoading && sorted.length === 0 && <EmptyUsers />}

        {!isLoading && sorted.length > 0 && (
          <Group>
            {sorted.map((u, i) => (
              <div key={u.id}>
                <UserRow
                  user={u}
                  isSelf={u.id === me?.id}
                  onChangeRole={(role) =>
                    updateRole.mutate({ userID: u.id, role })
                  }
                  onDelete={() => deleteUser.mutate(u.id)}
                />
                {i < sorted.length - 1 && <RowSeparator inset={56} />}
              </div>
            ))}
          </Group>
        )}
      </div>
    </AppShell>
  );
}

function UserRow({
  user,
  isSelf,
  onChangeRole,
  onDelete,
}: {
  user: User;
  isSelf: boolean;
  onChangeRole: (role: UserRole) => void;
  onDelete: () => void;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const joined = useRelativeTime(user.created_at);

  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <IconBadge tint={ROLE_TINT[user.role]} size={28}>
        {user.role === "admin" ? <Shield size={15} /> : <Mail size={15} />}
      </IconBadge>

      <div className="flex-1 min-w-0">
        <div
          className="text-body truncate"
          style={{ color: "var(--label-primary)" }}
          title={user.email}
        >
          {user.email}
          {isSelf && (
            <span
              className="ml-2 inline-block text-caption-2 px-1.5 py-0.5"
              style={{
                borderRadius: 6,
                color: "var(--system-blue)",
                background: "color-mix(in oklab, var(--system-blue) 14%, transparent)",
                fontWeight: 600,
                verticalAlign: "middle",
              }}
            >
              You
            </span>
          )}
        </div>
        <div
          className="text-footnote truncate mt-0.5"
          style={{ color: "var(--label-secondary)" }}
        >
          Joined {joined} · #{user.id}
        </div>
      </div>

      <RoleMenu
        role={user.role}
        disabled={isSelf}
        disabledReason={isSelf ? "You can't change your own role" : undefined}
        onChange={onChangeRole}
      />

      <button
        type="button"
        onClick={() => setConfirmOpen(true)}
        disabled={isSelf}
        aria-label={`Delete ${user.email}`}
        className="grid place-items-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          color: "var(--system-red)",
        }}
        title={isSelf ? "You can't delete yourself" : `Delete ${user.email}`}
      >
        <Trash2 size={16} strokeWidth={2} />
      </button>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete this user?"
        body={
          <>
            <span style={{ color: "var(--label-primary)" }}>{user.email}</span> will
            be removed and any devices they own will be unclaimed. This can't be undone.
          </>
        }
        confirmLabel="Delete user"
        destructive
        onConfirm={onDelete}
      />
    </div>
  );
}

function RoleMenu({
  role,
  disabled,
  disabledReason,
  onChange,
}: {
  role: UserRole;
  disabled?: boolean;
  disabledReason?: string;
  onChange: (next: UserRole) => void;
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          title={disabled ? disabledReason : `Change role (currently ${ROLE_LABEL[role]})`}
          className="inline-flex items-center gap-1 transition-colors disabled:cursor-not-allowed"
          style={{
            padding: "4px 9px 4px 10px",
            borderRadius: 999,
            background:
              role === "admin"
                ? "color-mix(in oklab, var(--system-purple) 16%, transparent)"
                : role === "support"
                  ? "color-mix(in oklab, var(--system-teal) 16%, transparent)"
                  : "color-mix(in oklab, var(--system-blue) 14%, transparent)",
            color:
              role === "admin"
                ? "var(--system-purple)"
                : role === "support"
                  ? "var(--system-teal)"
                  : "var(--system-blue)",
            fontWeight: 600,
            fontSize: 12,
            letterSpacing: "0.01em",
            opacity: disabled ? 0.5 : 1,
          }}
        >
          {ROLE_LABEL[role]}
          {!disabled && <ChevronDown size={12} strokeWidth={2.5} />}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="material z-50 min-w-[160px] p-1"
          style={{
            borderRadius: 12,
            border: "0.5px solid var(--separator)",
            boxShadow: "var(--shadow-elevated)",
          }}
        >
          {(["owner", "support", "admin"] as UserRole[]).map((r) => {
            const active = r === role;
            return (
              <DropdownMenu.Item
                key={r}
                disabled={active}
                onSelect={() => onChange(r)}
                className="flex items-center gap-2 px-3 py-2 text-callout cursor-pointer outline-none data-[highlighted]:bg-[var(--fill-quaternary)] data-[disabled]:opacity-60 data-[disabled]:cursor-default"
                style={{ borderRadius: 8, color: "var(--label-primary)" }}
              >
                <span style={{ width: 14, height: 14, display: "inline-flex" }}>
                  {active ? <Check size={14} strokeWidth={2.5} /> : null}
                </span>
                {ROLE_LABEL[r]}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function UsersListSkeleton() {
  return (
    <Group>
      {[0, 1, 2, 3].map((i) => (
        <div key={i}>
          <div className="flex items-center gap-3 px-4 py-3">
            <Skeleton width={28} height={28} rounded={8} />
            <div className="flex-1 space-y-2">
              <Skeleton width="60%" height={16} rounded={4} />
              <Skeleton width="35%" height={12} rounded={4} />
            </div>
            <Skeleton width={68} height={22} rounded={999} />
            <Skeleton width={32} height={32} rounded={8} />
          </div>
          {i < 3 && <RowSeparator inset={56} />}
        </div>
      ))}
    </Group>
  );
}

function EmptyUsers() {
  return (
    <div className="flex-1 flex items-center justify-center min-h-[40vh] py-12">
      <div className="text-center max-w-sm px-4">
        <span
          aria-hidden
          className="grid place-items-center mx-auto mb-5"
          style={{
            width: 64,
            height: 64,
            borderRadius: 18,
            background: "var(--fill-tertiary)",
            color: "var(--label-secondary)",
          }}
        >
          <UsersIcon size={28} />
        </span>
        <div className="text-title-3" style={{ color: "var(--label-primary)" }}>
          No users yet
        </div>
        <p className="text-body mt-2" style={{ color: "var(--label-secondary)" }}>
          Account holders will appear here as they sign up.
        </p>
      </div>
    </div>
  );
}
